import React, { useEffect, useRef, useState } from 'react';
import Layout from '@/components/layout/Layout';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Upload, FileText, CheckCircle2, Users, Phone, AlertTriangle, History } from 'lucide-react';

const Stat = ({ label, value, tone = 'slate' }) => (
  <div className={`rounded-lg border p-3 bg-${tone}-50 border-${tone}-200`}>
    <div className="text-xs text-slate-500">{label}</div>
    <div className="text-2xl font-bold">{value}</div>
  </div>
);

const errMsg = (d, fallback) => {
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join('; ');
  return fallback;
};

const apiRoot = () => `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/v1`;

const uploadForm = async (path, fd) => {
  const res = await fetch(`${apiRoot()}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'X-Tenant-ID': localStorage.getItem('active_tenant_id') || '',
    },
    body: fd,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(errMsg(data.detail, `Fehler ${res.status}`));
  return data;
};

export default function FileMakerImportPage() {
  const [mode, setMode] = useState('kontakte');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const fileInputRef = useRef(null);
  const tenantId = localStorage.getItem('active_tenant_id');

  const loadHistory = async () => {
    try {
      const res = await fetch(`${apiRoot()}/filemaker-contacts/history?tenant_id=${tenantId}`, {
        credentials: 'include',
      });
      if (res.ok) setHistory(await res.json());
    } catch { /* still */ }
  };
  useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, []);

  const base = mode === 'kontakte' ? '/filemaker-contacts' : '/filemaker-contacts/belegungen';

  const doPreview = async () => {
    if (!file) { toast.error('Bitte zuerst eine CSV- oder Excel-Datei (.xlsx) wählen'); return; }
    setLoading(true); setPreview(null); setResult(null);
    try {
      const fd = new FormData();
      fd.append('tenant_id', tenantId);
      fd.append('file', file);
      const data = await uploadForm(`${base}/preview`, fd);
      setPreview(data);
    } catch (e) {
      toast.error(e.message || 'Vorschau fehlgeschlagen');
    } finally { setLoading(false); }
  };

  const doCommit = async () => {
    const suspicious = mode === 'belegungen'
      ? (preview.kz_match_percent < 50)
      : (preview.new_students > preview.total / 2);
    if (suspicious) {
      const ok = window.confirm(
        `ACHTUNG: Diese Datei scheint NICHT zum Mandanten „${preview.tenant_name}" zu passen ` +
        `(nur ${mode === 'belegungen' ? preview.kz_match_percent + '% der Kassenzeichen bekannt' : preview.new_students + ' von ' + preview.total + ' wären Neuanlagen'}).\n\n` +
        'Wirklich in diesen Mandanten importieren?'
      );
      if (!ok) return;
    }
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append('tenant_id', tenantId);
      fd.append('token', preview.token);
      if (mode === 'kontakte') fd.append('create_new_students', 'true');
      const data = await uploadForm(`${base}/commit`, fd);
      setResult(data);
      setPreview(null);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      toast.success('Import erfolgreich abgeschlossen');
      if (mode === 'kontakte') {
        toast.info(
          'Kontakte allein ändern keine Belegungen/Status. Als Nächstes: Belegungen mit derselben Excel importieren.',
          { duration: 8000 },
        );
      }
      loadHistory();
    } catch (e) {
      toast.error(e.message || 'Import fehlgeschlagen');
    } finally { setCommitting(false); }
  };

  const lastBelegungen = history.find((h) => h.import_type === 'belegungen');
  const lastKontakte = history.find((h) => h.import_type === 'kontakte');
  const belegungenStaleHint = Boolean(
    lastKontakte && (!lastBelegungen || String(lastBelegungen.created_at) < String(lastKontakte.created_at)),
  );

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="filemaker-import-page">
        <div>
          <h1 className="text-2xl font-bold">FileMaker-Import</h1>
          <p className="text-sm text-slate-500">
            Immer beide Schritte: zuerst Kontakte, dann Belegungen (gleiche iMikel_Komplett.xlsx).
            Nur Kontakte aktualisieren Telefon/E-Mail — aktive Kurse und „beendet“ kommen nur aus dem Belegungen-Import.
          </p>
        </div>

        {belegungenStaleHint && (
          <div
            className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2"
            data-testid="fm-belegungen-stale-hint"
          >
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold">Belegungen-Import fehlt oder ist älter als der letzte Kontakte-Import</div>
              <div className="mt-1">
                Ohne aktuellen Belegungen-Abgleich bleiben Status/Austritte veraltet (z.&nbsp;B. fälschlich „beendet“).
                {' '}
                <button
                  type="button"
                  className="underline font-medium"
                  onClick={() => { setMode('belegungen'); setPreview(null); setResult(null); }}
                >
                  Jetzt Belegungen wählen
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { key: 'kontakte', title: 'Kontakte (Gesamtexport)', desc: 'Telefon, E-Mail & Neuanmeldungen – FileMaker-Freitext oder iMikel_Komplett.xlsx (Blatt Teilnehmer)' },
            { key: 'belegungen', title: 'Belegungen (Export Belegungen)', desc: 'Kurse, Lehrer, Ein-/Austritte – Semikolon-CSV, XLSX (Blatt Belegungen) oder derselbe Gesamtexport wie bei Kontakte' },
          ].map((m) => (
            <button key={m.key} type="button" data-testid={`fm-mode-${m.key}`}
                    onClick={() => { setMode(m.key); setPreview(null); setResult(null); }}
                    className={`text-left rounded-lg border-2 p-4 transition-colors ${mode === m.key ? 'border-brand-red bg-brand-red/5' : 'border-slate-200 hover:border-slate-300'}`}>
              <div className="font-semibold text-sm">{m.title}</div>
              <div className="text-xs text-slate-500 mt-1">{m.desc}</div>
            </button>
          ))}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Upload className="w-4 h-4" /> 1. Datei hochladen</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-brand-red/40 text-sm">
              <FileText className="w-4 h-4 text-slate-400" />
              {file ? file.name : 'CSV- oder Excel-Datei wählen…'}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                className="hidden"
                data-testid="fm-import-file"
                onClick={(e) => { e.currentTarget.value = ''; }}
                onChange={(e) => { setFile(e.target.files[0] || null); setPreview(null); setResult(null); }}
              />
            </label>
            <Button onClick={doPreview} disabled={!file || loading} data-testid="fm-import-preview-btn">
              {loading ? 'Analysiere…' : 'Vorschau erstellen'}
            </Button>
          </CardContent>
        </Card>

        {preview && mode === 'belegungen' && (
          <Card data-testid="fm-import-preview">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> 2. Vorschau (noch nichts gespeichert)</CardTitle>
              <CardDescription>
                {preview.rows_total} Belegungen in der Datei erkannt · Ziel-Mandant: <b>{preview.tenant_name}</b> · {preview.kz_match_percent}% der Kassenzeichen in diesem Mandanten bekannt
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {preview.kz_match_percent < 50 && (
                <div className="rounded-lg border-2 border-red-300 bg-red-50 p-3 text-sm text-red-700 flex items-center gap-2" data-testid="fm-tenant-mismatch-warning">
                  <AlertTriangle className="w-5 h-5 shrink-0" />
                  <span>Nur {preview.kz_match_percent}% der Kassenzeichen sind in „{preview.tenant_name}" bekannt – diese Datei gehört vermutlich zu einem <b>anderen Mandanten</b>! Bitte oben links den richtigen Mandanten wählen.</span>
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Stat label="Belegungen in Datei" value={preview.rows_total} />
                <Stat label="Bereits vorhanden (werden abgeglichen)" value={preview.enrollments_matched} />
                <Stat label="Neue Teilnehmer (werden angelegt)" value={preview.students_to_create} />
              </div>
              {preview.missing_samples.length > 0 && (
                <div>
                  <div className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4" /> Teilnehmer, die neu angelegt werden (max. 50 angezeigt)
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {preview.missing_samples.map((s) => (
                      <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                    ))}
                  </div>
                </div>
              )}
              <div className="text-xs text-amber-700 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Beim Import werden außerdem Belegungen beendet, die im Export nicht mehr vorkommen, und Schüler-Status neu gesetzt (aktiv/ehemalig).
              </div>
              <Button onClick={doCommit} disabled={committing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="fm-import-commit-btn">
                {committing ? 'Importiere…' : 'Abgleich jetzt ausführen'}
              </Button>
            </CardContent>
          </Card>
        )}

        {result && mode === 'belegungen' && (
          <Card data-testid="fm-import-result" className="border-emerald-300">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2 text-emerald-700"><CheckCircle2 className="w-4 h-4" /> Abgleich abgeschlossen</CardTitle>
              <CardDescription>Aktive Belegungen jetzt: <b>{result.active_enrollments_now}</b> (Soll laut Datei: {result.rows_total})</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Stat label="Belegungen neu" value={result.enrollments_created} />
              <Stat label="Belegungen aktualisiert" value={result.enrollments_updated} />
              <Stat label="Belegungen beendet (veraltet)" value={result.enrollments_closed_stale} />
              <Stat label="Teilnehmer neu angelegt" value={result.students_created} />
              <Stat label="Teilnehmer reaktiviert" value={result.students_reactivated} />
              <Stat label="Kurse neu" value={result.courses_created} />
            </CardContent>
          </Card>
        )}

        {preview && mode === 'kontakte' && (
          <Card data-testid="fm-import-preview">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> 2. Vorschau (noch nichts gespeichert)</CardTitle>
              <CardDescription>{preview.total} Kassenzeichen in der Datei erkannt · Ziel-Mandant: <b>{preview.tenant_name}</b></CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {preview.new_students > preview.total / 2 && (
                <div className="rounded-lg border-2 border-red-300 bg-red-50 p-3 text-sm text-red-700 flex items-center gap-2" data-testid="fm-tenant-mismatch-warning">
                  <AlertTriangle className="w-5 h-5 shrink-0" />
                  <span>{preview.new_students} von {preview.total} Datensätzen wären Neuanlagen – diese Datei gehört vermutlich zu einem <b>anderen Mandanten</b>! Bitte oben links den richtigen Mandanten wählen.</span>
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Kontakte nachtragbar" value={preview.contact_updates} />
                <Stat label="Neue Schüler" value={preview.new_students} />
                <Stat label="Duplikate (übersprungen)" value={preview.duplicates} />
                <Stat label="Ohne verwertbare Daten" value={preview.no_data} />
              </div>

              {preview.new_students_list.length > 0 && (
                <div>
                  <div className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4" /> Neue Schüler ({preview.new_students_list.length}) – werden als „aktiv" angelegt
                  </div>
                  <div className="border rounded-lg overflow-auto max-h-72">
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>KZ</TableHead><TableHead>Name</TableHead><TableHead>Geb.</TableHead>
                        <TableHead>Adresse</TableHead><TableHead>Zahler</TableHead><TableHead>Kontakt</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {preview.new_students_list.map((p) => (
                          <TableRow key={p.kz}>
                            <TableCell className="font-mono text-xs">{p.kz}</TableCell>
                            <TableCell className="text-sm font-medium">{p.name}</TableCell>
                            <TableCell className="text-xs">{p.dob ? p.dob.split('-').reverse().join('.') : '–'}</TableCell>
                            <TableCell className="text-xs">{p.address}</TableCell>
                            <TableCell className="text-xs">{p.zahler}</TableCell>
                            <TableCell className="text-xs">{[p.phone, p.email].filter(Boolean).join(' · ')}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {preview.duplicates_list.length > 0 && (
                <div>
                  <div className="text-sm font-semibold mb-2 flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="w-4 h-4" /> Mögliche Duplikate – werden übersprungen
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {preview.duplicates_list.map((p) => (
                      <Badge key={p.kz} variant="outline" className="text-xs">{p.kz} {p.name}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {preview.contact_samples.length > 0 && (
                <div>
                  <div className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <Phone className="w-4 h-4" /> Beispiele: Kontakte, die nachgetragen werden (nur leere Felder)
                  </div>
                  <div className="space-y-1">
                    {preview.contact_samples.map((p) => (
                      <div key={p.kz} className="text-xs text-slate-600">
                        <span className="font-mono">{p.kz}</span> {p.name}: {[p.phone, p.email].filter(Boolean).join(' · ') || '–'}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Button onClick={doCommit} disabled={committing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="fm-import-commit-btn">
                {committing ? 'Importiere…' : 'Import jetzt ausführen'}
              </Button>
            </CardContent>
          </Card>
        )}

        {result && mode === 'kontakte' && (
          <Card data-testid="fm-import-result" className="border-emerald-300">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2 text-emerald-700"><CheckCircle2 className="w-4 h-4" /> Import abgeschlossen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Familien aktualisiert" value={result.families_updated} />
                <Stat label="Zahler aktualisiert" value={result.guardians_updated} />
                <Stat label="Schüler neu angelegt" value={result.students_created} />
                <Stat label="Duplikate übersprungen" value={result.skipped_duplicates} />
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex flex-wrap items-center gap-3">
                <span>Nächster Schritt: Belegungen importieren (Status, Kurse, Ein-/Austritte).</span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-amber-400"
                  data-testid="fm-goto-belegungen-btn"
                  onClick={() => {
                    setMode('belegungen');
                    setResult(null);
                    setPreview(null);
                    setFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                >
                  Zu Belegungen wechseln
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {history.length > 0 && (
          <Card data-testid="fm-import-history">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4" /> Import-Historie</CardTitle>
              <CardDescription>Letzte Importe für diesen Mandanten (Audit)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-auto max-h-80">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Datum</TableHead><TableHead>Benutzer</TableHead><TableHead>Typ</TableHead>
                    <TableHead>Datei</TableHead><TableHead>Ergebnis</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {history.map((h) => (
                      <TableRow key={h.id}>
                        <TableCell className="text-xs whitespace-nowrap">{h.created_at}</TableCell>
                        <TableCell className="text-xs">{h.username || '–'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{h.import_type === 'kontakte' ? 'Kontakte' : 'Belegungen'}</Badge>
                        </TableCell>
                        <TableCell className="text-xs max-w-[220px] truncate">{h.filename || '–'}</TableCell>
                        <TableCell className="text-xs text-slate-600">
                          {h.import_type === 'kontakte'
                            ? `${h.stats.students_created ?? 0} Schüler neu · ${h.stats.guardians_updated ?? 0} Zahler · ${h.stats.families_updated ?? 0} Familien`
                            : `${h.stats.enrollments_created ?? 0} neu · ${h.stats.enrollments_updated ?? 0} akt. · ${h.stats.enrollments_closed_stale ?? 0} beendet · aktiv: ${h.stats.active_enrollments_now ?? '–'}`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
